import { CellClassParams, ColDef, ICellRendererParams } from "ag-grid-community";
import clsx from "classnames";
import { safeParseInt } from "lib/number";
import {
    ColumnTypes,
    GridShipmentRowCallbackParams,
    IRowData,
    ISize,
    ShipmentCellValueChangeEvent,
    SizeData,
} from "../types";
import { Editors } from "constants/product-collection";
import { updateWorkingOrder } from "actions/cart-legacy";
import { postToWindow } from "helpers/window-messages";
import { getWOUpdateSizeAction } from "containers/legacy-cart/helpers";
import { isMatchLocation } from "helpers/legacy-cart/bulk";
import { isEqualBy } from "helpers/common";
import type { ISizeHeaderPrices } from "types/cart/shipment-review";
import { CustomDetailsHeader } from "containers/legacy-cart/grid-components/CustomDetailsHeader";

// sizingGrid.styl
export const getCellClasses = (pinned: boolean | string) => ["cell--shipment", pinned ? "cell-footer" : "cell-data"];

export const generateShipmentCellClasses = (data: IRowData, colId: string, isFooter: boolean): string[] => {
    const sizeData = data.sizes?.[colId];
    return [
        ...getCellClasses(isFooter),
        isEditable(data, colId) ? "cell-enabled" : "cell-disabled",
        ...(sizeData && sizeData.isOverordered ? ["overordered"] : []),
    ];
};

export interface IOptions {
    pinnedFooterRowRenderer?: string;
    cellRenderer?: string;
    headerParams?: { priceDetails: ISizeHeaderPrices };
    rendererParams?: Record<string, any> | (<P extends ICellRendererParams>(params: P) => ICellRendererParams);
}

export const buildSizeColDef = (sizeObject: ISize, options?: IOptions): ColDef => ({
    headerName: sizeObject.name,
    colId: sizeObject.id,
    type: ColumnTypes.Size,
    filter: false,
    suppressMenu: true,
    suppressColumnsToolPanel: true,
    suppressMovable: true,
    resizable: true,
    cellEditor: Editors.SizeCellEditor,
    headerClass: "right-aligned size-cell",
    headerComponentParams: options?.headerParams,
    headerComponent: options?.headerParams && CustomDetailsHeader,
    cellClass: ({ data, colDef, node }: CastProp<CellClassParams, "data", IRowData>) =>
        clsx(generateShipmentCellClasses(data, colDef.colId, !!node.rowPinned), "right-aligned"),
    cellRendererSelector: (params) => ({
        component:
            params.node.rowPinned === "bottom"
                ? options?.pinnedFooterRowRenderer || Editors.PinnedFooterModalRowRenderer
                : options?.cellRenderer || Editors.SizeRenderer,
    }),
    cellRendererParams: options?.rendererParams,
    width: 96,
    editable: (params) => isEditable(params.data, params.colDef.colId),
    cellStyle: {
        paddingLeft: 0,
        paddingRight: 0,
    },
    equals: isEqualBy<SizeData>(["quantity", "available", "allocated", "isVirtual", "isOverordered"]), // list of values to check for re-rendering the Size Cell
    valueGetter: ({ data }: GridShipmentRowCallbackParams) => data.sizes?.[sizeObject.id],
    valueSetter: (params) => {
        const { data, newValue } = params as GridShipmentRowCallbackParams<typeof params>;
        const newQuantity = safeParseInt(newValue, 0) || 0;
        const sizeData = data.sizes[sizeObject.id];
        if (sizeData.quantity === newQuantity) {
            return false;
        }
        data.sizes[sizeObject.id] = {
            ...sizeData,
            quantity: newQuantity,
        };
        return true;
    },
    onCellValueChanged: ({ data, newValue, oldValue }: ShipmentCellValueChangeEvent) =>
        newValue?.quantity !== oldValue?.quantity &&
        postToWindow(updateWorkingOrder(getWOUpdateSizeAction(data.item, newValue.size, newValue.quantity))),
});

const isEditable = (data: IRowData, id: string) => !!data.sizes?.[id] && data.available && isMatchLocation(data);
